import React, { useEffect } from 'react';
import {
  Platform,
  StyleSheet,
  Text,
  View,
  Image,
  FlatList,
  TouchableOpacity,
} from 'react-native';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';

import { usePlusContractsQuery } from 'apollo/queries/autogenerate/hooks';
import {
  PlusContract,
  PlusContractStatus,
} from 'apollo/queries/autogenerate/schemas';
import Loading from 'shared/modules/common/components/atoms/Loading';
import colors from 'shared/modules/common/constants/colors';
import typography from 'shared/modules/common/constants/typography';
import generateID from 'shared/modules/common/utils/generate-id';
import { ACTION, ITEM, SCREEN } from 'shared/modules/usage/constants/mixpanel';
import { formatYearMonthDate, formatToCurrency } from 'shared/utils/japanese';
import Mixpanel from 'shared/vendor/Mixpanel';
import NavigationBar from 'web/modules/common/components/molecules/NavigationBar';
import routes from 'web/navigation/routes';

export default function PlusContractsScreen() {
  const navigate = useNavigate();
  const isBigScreen = useMediaQuery({ minWidth: 768 });
  const { data: contracts, loading: isLoading } = usePlusContractsQuery({
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    const eventName = `${SCREEN.PLUS_CONTRACTS} - ${ACTION.PAGE_VIEW}`;

    Mixpanel.timeEvent(eventName);

    return () => Mixpanel.trackEvent(eventName);
  }, []);

  if (isLoading && !contracts) {
    return <Loading id="loading-indicator" />;
  }

  if (!contracts || !contracts.plusContracts) {
    return null;
  }

  const handlePressContractItem = (selected: PlusContract) => {
    Mixpanel.trackEvent(`${SCREEN.PLUS_CONTRACTS} - ${ACTION.CLICK}`, {
      Item: ITEM.CONTRACT,
    });

    navigate(
      routes.authenticated.usage.plusContractDetail.replace(':id', selected.id)
    );
  };

  return (
    <View style={[styles.screen, isBigScreen && styles.screenBigScreen]}>
      <View
        style={[styles.container, isBigScreen && styles.containerBigScreen]}
      >
        <NavigationBar
          title="超あと払い一覧"
          onBackButtonPress={() => navigate(-1)}
          id="navigation-bar"
        />
        <FlatList
          data={contracts.plusContracts}
          keyExtractor={(item) => item.id}
          renderItem={(item) => {
            return (
              <Item
                contract={item.item}
                isTop={item.index === 0}
                isBottom={
                  item.index === (contracts?.plusContracts.length || 0) - 1
                }
                onPress={handlePressContractItem}
                id={`plus-contracts-item-${item.index}`}
              />
            );
          }}
          ItemSeparatorComponent={() => (
            <View style={styles.separatorContainer}>
              <View style={styles.separator} />
            </View>
          )}
          ListEmptyComponent={<Empty />}
        />
      </View>
    </View>
  );
}

const Item = ({
  contract,
  isTop,
  isBottom,
  onPress,
  id,
}: {
  contract: PlusContract;
  isTop: boolean;
  isBottom: boolean;
  onPress: (selected: PlusContract) => void;
  id?: string;
}) => {
  const topRadius = isTop ? 8 : 0;
  const bottomRadius = isBottom ? 8 : 0;
  const contractedAt = contract.contractedAt ?? contract.createdAt;

  return (
    <TouchableOpacity
      onPress={() => onPress(contract)}
      /** Generate HTML and Test IDs  */
      {...generateID(id)}
      /** Platform-specific props */
      {...Platform.select({
        web: {
          accessibilityRole: 'button',
        },
      })}
    >
      <View
        style={[
          styles.itemContainer,
          {
            borderTopStartRadius: topRadius,
            borderTopEndRadius: topRadius,
            borderBottomStartRadius: bottomRadius,
            borderBottomEndRadius: bottomRadius,
          },
        ]}
      >
        <Text style={styles.itemTitle}>
          契約日 {formatYearMonthDate(contractedAt, 'en')}
        </Text>
        <View style={{ flex: 1, paddingHorizontal: 16 }}>
          <Text style={styles.itemAmount}>
            {formatToCurrency(contract.amount)}
          </Text>
          {contract.status === PlusContractStatus.PENDING && (
            <Text style={styles.itemBadge}>申込中</Text>
          )}
        </View>
        <View>
          <Image
            style={styles.caret}
            source={require('assets/images/icons/caret-right.png')}
          />
        </View>
      </View>
    </TouchableOpacity>
  );
};

const Empty = () => {
  return (
    <View style={styles.emptyContainer}>
      <Text style={styles.emptyText}>申込中の超あと払いはありません</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  screen: {
    alignItems: 'center',
    padding: 16,
  },
  screenBigScreen: {
    padding: 24,
  },
  container: {
    width: '100%',
  },
  containerBigScreen: {
    maxWidth: 960,
  },
  itemContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.white,
    padding: 16,
  },
  itemTitle: {
    ...typography.caption01,
    fontSize: 13,
    fontWeight: '700',
    color: colors.greyDark,
  },
  itemAmount: {
    ...typography.title03,
    fontSize: 16,
    fontWeight: '700',
    color: colors.greyDark,
    textAlign: 'right',
  },
  itemBadge: {
    ...typography.caption02,
    fontSize: 13,
    fontWeight: '400',
    color: colors.grey,
    textAlign: 'right',
  },
  caret: {
    width: 16,
    height: 16,
    tintColor: colors.greyLight,
  },
  emptyContainer: {
    alignContent: 'center',
    justifyContent: 'center',
    backgroundColor: colors.white,
    borderRadius: 8,
  },
  emptyText: {
    ...typography.caption02,
    fontSize: 13,
    color: colors.greyMedium,
    textAlign: 'center',
    marginVertical: 22,
  },
  separatorContainer: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: colors.white,
  },
  separator: {
    flex: 1,
    height: 1,
    marginHorizontal: 16,
    backgroundColor: colors.greySuperLight,
  },
});
